import { WarningCircleIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import { SelectBox } from '../../../components/input/select-box.tsx'
import { TextArea } from '../../../components/input/text-area.tsx'
import { TextInput } from '../../../components/input/text-input.tsx'
import * as styles from './integration-drawer.css.ts'
import {
  getIsFieldRequired,
  getIsHttpUrl,
  parseWorkItemId,
  type IntegrationField,
} from './integration-instance-utils.ts'

export const INVALID_URL_MESSAGE = 'Enter a full URL, including https://'

export const INVALID_POSITIVE_INTEGER_MESSAGE = 'Enter a whole number greater than zero.'

/**
 * Whether a field's value would survive the request schema, which declares the host fields
 * `format: uri` and the work item ID `int64`. Caught here as a field error the user can act on,
 * rather than in the mutation where it would appear as a parse failure with nothing naming the
 * field. A blank value is left to the required check, which has its own message — and on Manage to
 * the erase rule, where blanking an optional field is a deliberate clear rather than a mistake.
 *
 * `getIsHttpUrl` rather than `URL.canParse`, so the check means what the message says: a scheme
 * Pixee cannot fetch is rejected here rather than stored and later rendered as a link.
 *
 * The integer check is what keeps `parseWorkItemId`'s drop from being silent. That function reads an
 * unparseable entry as "no ID" by design, calling itself "the last line rather than the report" —
 * this is the report. It matters most on an update: an omitted field leaves a merging PUT keeping
 * the stored ID, so without this the drawer would accept `#4821`, change nothing, and close on a
 * success toast.
 */
export const getFieldFormatError = (field: IntegrationField, value: string | undefined): string | undefined => {
  const trimmed = (value ?? '').trim()
  if (trimmed === '') {
    return undefined
  }
  if (field.isUrl && !getIsHttpUrl(trimmed)) {
    return INVALID_URL_MESSAGE
  }
  if (field.isPositiveInteger && parseWorkItemId(trimmed) === undefined) {
    return INVALID_POSITIVE_INTEGER_MESSAGE
  }
  return undefined
}

type IntegrationFieldInputProps = {
  field: IntegrationField
  /** Namespaced by the drawer so a Setup and a Manage field never share an id. */
  id: string
  value: string
  onChange: (value: string) => unknown
  onBlur?: () => void
  /**
   * Overrides the field's own requirement. The Manage drawer marks every credential required for a
   * save that moves the endpoint, and leaves everything else at what the field declares.
   */
  isRequired?: boolean
  /** Overrides the field's own helper text; the error text still replaces whichever is shown. */
  helperText?: string
  placeholder?: string
  errorText?: string
  /** Marks the field invalid without giving it a message, for the second and later anchored fields. */
  isError?: boolean
  /**
   * Closes the field to editing. For a value the platform will refuse to change whatever is typed —
   * a source provider's endpoint once repositories are onboarded through it. The caller supplies
   * `helperText` saying why; a field disabled without one says only that it is unavailable.
   */
  isDisabled?: boolean
  /** Overrides a select's own options, for a drawer that adds one of its own. */
  options?: { value: string; label: string }[]
}

/** One connection or credential field, rendered from its entry in `TOOL_FIELD_CONFIG`. */
export const IntegrationFieldInput: React.FC<IntegrationFieldInputProps> = ({
  field,
  id,
  value,
  onChange,
  onBlur,
  isRequired,
  helperText,
  placeholder,
  errorText,
  isError,
  isDisabled,
  options,
}) => {
  const resolvedIsRequired = isRequired ?? getIsFieldRequired(field)
  // `InputContainer` renders "*" on a required field and "(Optional)" on an optional one, and this
  // picks which of the two it may render. A field marked `optional-unmarked` gets neither: its
  // helper text already says what leaving it blank means.
  const explicitLabelCondition = !resolvedIsRequired && field.requirement === 'optional' ? 'optional' : 'required'
  const resolvedHelperText = helperText ?? field.helperText
  const resolvedErrorText = errorText ?? getFieldFormatError(field, value)

  return (
    match(field)
      .with({ kind: 'select' }, selectField => (
        <SelectBox
          id={id}
          label={selectField.label}
          isRequired={resolvedIsRequired}
          explicitLabelCondition={explicitLabelCondition}
          value={value}
          onValueChange={onChange}
          placeholder={placeholder}
          options={options ?? selectField.options ?? []}
          helperText={resolvedHelperText}
          errorText={resolvedErrorText}
          isError={isError}
          disabled={isDisabled}
        />
      ))
      .with({ kind: 'password-multiline' }, multilineField => (
        <TextArea
          id={id}
          label={multilineField.label}
          isMasked
          isRequired={resolvedIsRequired}
          explicitLabelCondition={explicitLabelCondition}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder ?? multilineField.placeholder}
          helperText={resolvedHelperText}
          errorText={resolvedErrorText}
          isError={isError}
          disabled={isDisabled}
        />
      ))
      // Spelled out rather than left to `.otherwise`, so that a kind added later fails to compile here
      // instead of silently rendering as a single-line input — which is the bug `password-multiline`
      // exists to fix (ISS-9101).
      .with({ kind: 'text' }, { kind: 'password' }, textField => (
        <TextInput
          id={id}
          label={textField.label}
          type={textField.kind === 'password' ? 'password' : 'text'}
          isRequired={resolvedIsRequired}
          explicitLabelCondition={explicitLabelCondition}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder ?? textField.placeholder}
          helperText={resolvedHelperText}
          errorText={resolvedErrorText}
          isError={isError}
          disabled={isDisabled}
        />
      ))
      .exhaustive()
  )
}

/**
 * A save failure that names no field the user can edit. `unexpected-response` is the reason this
 * exists: the tool answered, just not in a shape Pixee understands, so there is nothing to anchor to
 * and the report belongs above the form rather than beside a field.
 */
export const FormErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <p className={styles.formErrorBanner} role="alert">
    <WarningCircleIcon size={16} weight="fill" className={styles.formErrorBannerIcon} />
    <span>{message}</span>
  </p>
)
